import { cn } from "../../lib";

/**
 * System glass speech-bubble chrome — authoritative fills used by Platform Action
 * BlockQuote (step accordion / splash) and {@link SpeechBubble} glass path.
 *
 * Shell / mid-border CSS variables continue the GlassBorder frosted rim onto the
 * stacked arrow so the rim does not read as cutting across the tip.
 *
 * **Seamless union:** plate + shell hairline + arrow shell/mid fills are all
 * **opaque** `color-mix(…, var(--background))` — not `bg-black/6` translucency —
 * so the tip blends with the rim over veils / scrolling chat (same look as PA
 * when PA sits on opaque chrome).
 */

/** CSS custom properties for arrow shell + mid-border fills. */
export const SPEECH_BUBBLE_SYSTEM_ARROW_CSS_VARS_CLASS = cn(
	"[--sq-quote-arrow-shell:color-mix(in_srgb,black_6%,var(--background))]",
	"dark:[--sq-quote-arrow-shell:color-mix(in_srgb,white_20%,var(--background))]",
	"[--sq-quote-arrow-border:color-mix(in_srgb,var(--border)_40%,var(--background))]",
	"dark:[--sq-quote-arrow-border:color-mix(in_srgb,white_10%,var(--background))]",
);

/**
 * Opaque frosted-rim plate — same mixes as {@link SPEECH_BUBBLE_SYSTEM_ARROW_SHELL_FILL}.
 * Prefer over translucent `GlassBorder` system plate so tip/rim stay one shape.
 */
export const SPEECH_BUBBLE_SYSTEM_GLASS_PLATE_CLASS = cn(
	"bg-[color-mix(in_srgb,black_6%,var(--background))] shadow-xs",
	"dark:bg-[color-mix(in_srgb,white_20%,var(--background))] dark:shadow-xs",
);

/** Hairline on the GlassBorder inner shell — matches PA step accordion. */
export const SPEECH_BUBBLE_SYSTEM_SHELL_BORDER_CLASS = cn(
	"border border-[color-mix(in_srgb,black_6%,var(--background))]",
	"dark:border-[color-mix(in_srgb,white_20%,var(--background))]",
);

/**
 * Opaque face hairline — matches {@link SPEECH_BUBBLE_SYSTEM_ARROW_BORDER_FILL}
 * (not translucent `border-border/40`).
 */
export const SPEECH_BUBBLE_SYSTEM_FACE_BORDER_CLASS = cn(
	"border border-[color-mix(in_srgb,var(--border)_40%,var(--background))]",
	"dark:border-[color-mix(in_srgb,white_10%,var(--background))]",
);

export const SPEECH_BUBBLE_SYSTEM_ARROW_SHELL_FILL =
	"var(--sq-quote-arrow-shell)";
export const SPEECH_BUBBLE_SYSTEM_ARROW_BORDER_FILL =
	"var(--sq-quote-arrow-border)";
export const SPEECH_BUBBLE_SYSTEM_ARROW_FACE_FILL = "var(--background)";

/** Compact message-action bubble: thinner rim + smaller tip than full-width PA lists. */
export const SPEECH_BUBBLE_MESSAGE_ACTIONS_BORDER_WIDTH_PX = 4;
export const SPEECH_BUBBLE_MESSAGE_ACTIONS_ARROW_SCALE = 0.72;

/** Chat assistant avatar bubble: thinner glass rim than message-actions; tip points at the message. */
export const SPEECH_BUBBLE_MESSAGE_AVATAR_SIZE_PX = 32;
export const SPEECH_BUBBLE_MESSAGE_AVATAR_BORDER_WIDTH_PX = 2;
export const SPEECH_BUBBLE_MESSAGE_AVATAR_ARROW_SCALE = 0.55;
/** Tip points right toward message content (`arrowRotationDeg`). */
export const SPEECH_BUBBLE_MESSAGE_AVATAR_ARROW_ROTATION_DEG = 0;
/**
 * In-flow layout box for the avatar circle (face + glass band + shell hairline).
 * Right tip is absolutely positioned and does not add to this width.
 * Keep clone `ml` / name row `min-h` in sync with this.
 */
export const SPEECH_BUBBLE_MESSAGE_AVATAR_OUTER_SIZE_PX =
	SPEECH_BUBBLE_MESSAGE_AVATAR_SIZE_PX +
	2 * SPEECH_BUBBLE_MESSAGE_AVATAR_BORDER_WIDTH_PX +
	/** {@link GLASS_SHELL_BORDER_WIDTH_PX} — keep literal to avoid chrome↔border cycles. */
	2 * 1;
